import styled from '@emotion/styled'
import { color, font, screen, size } from '../../styles'
import { useRef } from 'react'
import { motion, useScroll } from 'framer-motion'

type Step = {
   label: string
   completed: boolean
   onClick?: () => void
}
interface Props {
   steps: Step[]
   activeStep: number
   clickable?: boolean
   className?: string
   collapsibleOnScroll?: boolean
   scrollTopHeight?: number
}

const NavigationSteps: React.FC<Props> = (props) => {
   const { steps, clickable, activeStep, className, collapsibleOnScroll, scrollTopHeight } = props
   const holderRef = useRef(null)
   const { scrollY } = useScroll({
      target: holderRef,
      layoutEffect: true,
   })

   const cssVariables = {
      '--active-line-width-percentage': `${(activeStep / (steps.length - 1)) * 100}%`,
      '--completed-steps-percentage': `${
         ((steps.filter((step) => step.completed).length - 1) / (steps.length - 1)) * 100
      }%`,
      '--step-width-percentage': `${100 / steps.length}%`,
      '--scroll-y': scrollY,
      '--scroll-top-height': `${scrollTopHeight || 0}px`,
   } as React.CSSProperties

   const classNames = (className || '') + (collapsibleOnScroll ? ' collapsible-on-scroll' : '')

   return (
      <Holder className={classNames} style={cssVariables} ref={holderRef}>
         <div className="labeled-steps">
            {steps.map((step, index) => {
               const isFirstStep = index === 0
               const previousStep = isFirstStep ? undefined : steps[index - 1]
               const isFirstCompleted = steps[0].completed
               const isClickable =
                  clickable && ((previousStep?.completed && isFirstCompleted) || isFirstStep)
               const stepClassNames = `step ${activeStep === index ? 'active' : ''} ${
                  step.completed ? 'completed' : ''
               } ${isClickable ? 'clickable' : ''}`

               return (
                  <div
                     key={index}
                     className={stepClassNames}
                     onClick={() => {
                        isClickable && step.onClick!()
                     }}
                  >
                     <div className="step-number">{index + 1}</div>
                     <div
                        className="step-label"
                        style={{
                           transformOrigin:
                              index === 0
                                 ? '25% 30%'
                                 : index === steps.length - 1
                                   ? '90% 30%'
                                   : '50% 30%',
                        }}
                     >
                        {step.label}
                     </div>
                  </div>
               )
            })}
         </div>
         <div className="sticky-steps" />
      </Holder>
   )
}

const Holder = styled(motion.div)`
   --transition-duration: 0.5s;
   --color: ${color.grayLight};
   --scroll-progress: clamp(0, calc(var(--scroll-y) / 200), 1);
   position: relative;

   .labeled-steps {
      display: flex;
      justify-content: space-between;
      gap: 1em;
      width: 100%;
      position: relative;
      top: 0;
      overflow: hidden;

      &::before {
         content: '';
         position: absolute;
         z-index: -2;
         top: 2em;
         left: 0;
         width: 100%;
         height: 2px;
         background: linear-gradient(
            to right,
            ${color.green} var(--completed-steps-percentage),
            ${color.grayLight} var(--completed-steps-percentage),
            ${color.grayLight} calc(100% - var(--active-line-width-percentage))
         );
         opacity: 0.4;
      }

      &::after {
         content: '';
         position: absolute;
         z-index: -1;
         top: 2em;
         left: 0;
         width: var(--active-line-width-percentage);
         height: 2px;
         background-color: ${color.green};
         transition: width var(--transition-duration);
      }

      .step {
         display: flex;
         flex-direction: column;
         align-items: center;
         gap: 1.5em;
         height: max-content;
         transition: border-color var(--transition-duration);
         width: var(--step-width-percentage);
         max-width: 7em;
         min-width: 4em;

         > * {
            transition: all var(--transition-duration);
            ${font.regular};
            user-select: none;
            width: 100%;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
         }

         .step-number {
            position: relative;
            top: 0.4em;
            font-size: ${size.m};
            font-weight: bold;
            color: ${color.white};
            background-color: var(--color);
            width: 2em;
            height: 2em;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            line-height: 1;
            ${font.thin};
            border: 10px solid ${color.white};
         }

         .step-label {
            height: 2em;
            font-size: 1em;
            color: var(--color);
         }

         &.clickable:not(.active) {
            cursor: pointer;
            &:hover {
               border-color: ${color.green};
            }
         }

         &:first-of-type::before {
            content: '';
            position: absolute;
            top: 29px;
            left: -90%;
            width: 100%;
            height: 10px;
            background-color: ${color.white};
            z-index: 0;
         }

         &:last-of-type::before {
            content: '';
            position: absolute;
            top: 29px;
            right: -90%;
            width: 100%;
            height: 10px;
            background-color: ${color.white};
            z-index: 0;
         }
      }
   }

   .sticky-steps {
      display: flex;
      justify-content: space-between;
      gap: 1em;
      width: calc(var(--scroll-progress) * 100vw);
      margin: 0;
      padding: 0;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 32px;
      transition: width 0.1s linear;
      &::before {
         content: '';
         position: absolute;
         z-index: -2;
         top: 0;
         left: 0;
         width: 100%;
         height: 4px;
         background: linear-gradient(
            to right,
            ${color.green} var(--completed-steps-percentage),
            ${color.grayLight} var(--completed-steps-percentage),
            ${color.grayLight} calc(100% - var(--active-line-width-percentage))
         );
         opacity: 0.4;
      }

      &::after {
         content: '';
         position: absolute;
         z-index: -1;
         top: 0;
         left: 0;
         width: var(--active-line-width-percentage);
         height: 4px;
         background-color: ${color.green};
         transition: width var(--transition-duration);
      }
   }

   /* Conditional styling */
   .step.completed {
      --color: ${color.green};
   }

   .step.active {
      --color: ${color.violet};
      & > * {
         scale: 1.3;
      }
   }

   &.collapsible-on-scroll {
      position: sticky;
      top: calc(49px + var(--scroll-top-height));

      .step {
         z-index: 3;
         transition: opacity var(--transition-duration) linear;
      }

      .labeled-steps {
         z-index: 2;
         opacity: calc(1 - var(--scroll-progress));
      }

      .sticky-steps {
         opacity: var(--scroll-progress);
      }
   }

   /* Mobile responsive */
   ${screen.onlyMobile} {
      .step {
         .step-label {
            font-size: 0.85em !important;
         }
         &.active {
            --color: ${color.violet};
            & > * {
               scale: 1.2;
            }
         }
      }

      --scroll-progress: clamp(0, calc(var(--scroll-y) / 120), 1);
   }
`

export default NavigationSteps
